/**
 * RiskMetrics component with dark theme styling.
 */

import { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { formatPercent } from '@/utils/formatters';
import type { Position, RiskMetrics as RiskMetricsType } from '@/types';

interface RiskMetricsProps {
  positions: Position[];
  isLoading?: boolean;
  concentrationThreshold?: number;
  nlv?: number | null;
}

export function calculateRiskMetrics(
  positions: Position[],
  threshold: number = 10,
  nlv?: number | null
): RiskMetricsType {
  if (positions.length === 0) {
    return {
      largest_position_percent: 0,
      largest_position_symbol: '-',
      top5_concentration: 0,
      positions_over_threshold: 0,
      concentrated_symbols: [],
    };
  }

  // Use NLV as denominator if available, otherwise return zeros (N/A)
  const denominator = nlv && nlv > 0 ? nlv : null;

  if (!denominator) {
    return {
      largest_position_percent: 0,
      largest_position_symbol: '-',
      top5_concentration: 0,
      positions_over_threshold: 0,
      concentrated_symbols: [],
    };
  }

  const positionsWithPercent = positions.map((p) => ({
    symbol: p.symbol,
    percent: (p.cost_basis / denominator) * 100,
  }));

  positionsWithPercent.sort((a, b) => b.percent - a.percent);

  const largest = positionsWithPercent[0];
  const top5 = positionsWithPercent.slice(0, 5);
  const top5Concentration = top5.reduce((sum, p) => sum + p.percent, 0);
  const concentrated = positionsWithPercent.filter((p) => p.percent > threshold);

  return {
    largest_position_percent: largest?.percent ?? 0,
    largest_position_symbol: largest?.symbol ?? '-',
    top5_concentration: top5Concentration,
    positions_over_threshold: concentrated.length,
    concentrated_symbols: concentrated.map((p) => p.symbol),
  };
}

function RiskBadge({ level }: { level: 'low' | 'medium' | 'high' }) {
  const styles = {
    low: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    high: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${styles[level]}`}>
      {level.toUpperCase()}
    </span>
  );
}

export function RiskMetrics({
  positions,
  isLoading = false,
  concentrationThreshold = 10,
  nlv = null,
}: RiskMetricsProps) {
  const metrics = useMemo(
    () => calculateRiskMetrics(positions, concentrationThreshold, nlv),
    [positions, concentrationThreshold, nlv]
  );

  const riskLevel = useMemo(() => {
    if (!nlv || nlv <= 0) return 'low'; // Can't assess risk without NLV
    if (metrics.largest_position_percent > 25 || metrics.positions_over_threshold > 3) {
      return 'high';
    }
    if (metrics.largest_position_percent > 15 || metrics.positions_over_threshold > 1) {
      return 'medium';
    }
    return 'low';
  }, [metrics, nlv]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Risk Metrics</CardTitle>
        <RiskBadge level={riskLevel} />
      </CardHeader>
      <CardContent>
        {!nlv || nlv <= 0 ? (
          <div className="py-4 text-center">
            <p className="text-sm text-dark-500">
              Set your Account Value above to see position size metrics.
            </p>
          </div>
        ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-dark-400">Largest Position</span>
            <span className="font-medium text-dark-200 font-mono">
              {metrics.largest_position_symbol}{' '}
              <span className="text-dark-400">({formatPercent(metrics.largest_position_percent)})</span>
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-dark-400">Top 5 Concentration</span>
            <span className="font-medium text-dark-200 font-mono">
              {formatPercent(metrics.top5_concentration)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-dark-400">
              Positions &gt; {concentrationThreshold}%
            </span>
            <span
              className={`font-medium font-mono ${
                metrics.positions_over_threshold > 0 ? 'text-amber-400' : 'text-dark-200'
              }`}
            >
              {metrics.positions_over_threshold}
            </span>
          </div>
          {metrics.concentrated_symbols.length > 0 && (
            <div className="pt-3 border-t border-dark-700">
              <p className="text-sm text-dark-400 mb-2">Concentrated Positions:</p>
              <div className="flex flex-wrap gap-2">
                {metrics.concentrated_symbols.map((symbol) => (
                  <span
                    key={symbol}
                    className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded-md border border-amber-500/20"
                  >
                    {symbol}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        )}
      </CardContent>
    </Card>
  );
}
